import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthUser } from '@/hooks/useAuth';

export type InterviewType = 'technical' | 'behavioral' | 'business_case';
export type InterviewMode = 'chat' | 'voice' | 'timed_problems';
export type InterviewDifficulty = 'junior' | 'pleno' | 'senior';
export type InterviewStatus = 'in_progress' | 'completed' | 'abandoned';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export interface InterviewFeedback {
  score_overall: number;
  scores: {
    comunicacao: number;
    tecnico: number;
    estrutura: number;
    exemplos: number;
  };
  pontos_fortes: string[];
  areas_melhoria: string[];
  dicas: string[];
  resumo: string;
}

export interface SimulationConfig {
  type: InterviewType;
  mode: InterviewMode;
  category?: string;
  difficulty: InterviewDifficulty;
}

export function useInterviewSimulation() {
  const { data: user, isLoading: isAuthLoading } = useAuthUser();

  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [simulationId, setSimulationId] = useState<string | null>(null);
  const [config, setConfig] = useState<SimulationConfig | null>(null);
  const [feedback, setFeedback] = useState<InterviewFeedback | null>(null);
  const [status, setStatus] = useState<InterviewStatus | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const startTimeRef = useRef<Date | null>(null);

  const isReady = status === 'in_progress' && !!simulationId;

  const checkSimulationLimit = useCallback(async (userId: string): Promise<{ canSimulate: boolean; remaining: number }> => {
    try {
      const { data, error } = await supabase.rpc('check_simulation_limit', {
        p_user_id: userId
      });

      if (error) {
        console.error('Error checking simulation limit:', error);
        return { canSimulate: false, remaining: 0 };
      }

      const result = data?.[0];
      return {
        canSimulate: result?.can_simulate ?? false,
        remaining: result?.simulations_remaining ?? 0
      };
    } catch (error) {
      console.error('Error checking simulation limit:', error);
      return { canSimulate: false, remaining: 0 };
    }
  }, []);

  const sendMessage = useCallback(async (
    content: string,
    isInitial = false,
    overrideConfig?: SimulationConfig,
    overrideSimulationId?: string,
    currentMessages?: Message[]
  ): Promise<boolean> => {
    const activeConfig = overrideConfig || config;
    const activeSimulationId = overrideSimulationId || simulationId;
    if (!activeConfig || !activeSimulationId) {
      console.error('sendMessage called without config or simulationId');
      return false;
    }

    setIsLoading(true);

    try {
      const baseMessages = currentMessages || messages;
      const newMessages = isInitial
        ? []
        : [...baseMessages, { role: 'user' as const, content }];

      if (!isInitial) {
        setMessages(newMessages);
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error('Sessão expirada. Faça login novamente.');
        return false;
      }

      const response = await fetch(
        `https://jdwcgbwcwkrrvaqtokju.supabase.co/functions/v1/interview-simulator-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            messages: newMessages,
            type: activeConfig.type,
            category: activeConfig.category,
            difficulty: activeConfig.difficulty,
            simulationId: activeSimulationId
          }),
        }
      );

      if (response.status === 429) {
        toast.error('Muitas requisições. Aguarde alguns segundos e tente novamente.');
        return false;
      }
      if (response.status === 402) {
        toast.error('Créditos da IA esgotados. Contate o suporte.');
        return false;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro na resposta da IA');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let textBuffer = '';

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;

          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              assistantContent += delta;
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: 'assistant', content: assistantContent }];
              });
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      const finalMessages = isInitial
        ? [{ role: 'assistant', content: assistantContent }]
        : [...newMessages, { role: 'assistant', content: assistantContent }];

      await supabase
        .from('interview_simulations')
        .update({ messages: finalMessages })
        .eq('id', activeSimulationId);

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar mensagem');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [config, simulationId, messages]);

  const startSimulation = useCallback(async (simulationConfig: SimulationConfig): Promise<boolean> => {
    if (!user) {
      toast.error('Você precisa estar logado para iniciar uma simulação');
      return false;
    }

    setIsInitializing(true);

    try {
      const { canSimulate } = await checkSimulationLimit(user.id);
      if (!canSimulate) {
        toast.error('Você atingiu o limite de simulações gratuitas deste mês');
        return false;
      }

      const { data, error } = await supabase
        .from('interview_simulations')
        .insert({
          user_id: user.id,
          type: simulationConfig.type,
          mode: simulationConfig.mode,
          category: simulationConfig.category,
          difficulty: simulationConfig.difficulty,
          status: 'in_progress',
          messages: []
        })
        .select()
        .single();

      if (error) throw error;

      await supabase.rpc('increment_simulation_count', { p_user_id: user.id });

      setSimulationId(data.id);
      setConfig(simulationConfig);
      setStatus('in_progress');
      setMessages([]);
      setFeedback(null);
      startTimeRef.current = new Date();

      // Pass config and id directly to avoid state race condition
      await sendMessage('', true, simulationConfig, data.id, []);

      return true;
    } catch (error) {
      console.error('Error starting simulation:', error);
      toast.error('Erro ao iniciar simulação');
      return false;
    } finally {
      setIsInitializing(false);
    }
  }, [user, checkSimulationLimit, sendMessage]);

  const endSimulation = useCallback(async () => {
    if (!simulationId || !config) return;

    const userMessages = messages.filter((m) => m.role === 'user');
    if (userMessages.length === 0) {
      const durationSeconds = startTimeRef.current
        ? Math.floor((new Date().getTime() - startTimeRef.current.getTime()) / 1000)
        : 0;
      setFeedback({
        score_overall: 0,
        scores: { comunicacao: 0, tecnico: 0, estrutura: 0, exemplos: 0 },
        pontos_fortes: ['Você deu o primeiro passo iniciando a simulação'],
        areas_melhoria: ['Responda ao menos uma pergunta para receber avaliação detalhada'],
        dicas: ['Reinicie a simulação e pratique respondendo com calma — não há pressa'],
        resumo: 'Simulação encerrada antes de responder. Comece novamente quando quiser praticar!',
      } as any);
      try {
        await supabase
          .from('interview_simulations')
          .update({ status: 'abandoned', duration_seconds: durationSeconds })
          .eq('id', simulationId);
      } catch (e) { console.error(e); }
      setStatus('completed');
      return;
    }


    setIsLoading(true);

    try {
      const durationSeconds = startTimeRef.current
        ? Math.floor((new Date().getTime() - startTimeRef.current.getTime()) / 1000)
        : 0;

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      const response = await fetch(
        `https://jdwcgbwcwkrrvaqtokju.supabase.co/functions/v1/interview-feedback-generator`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            messages,
            type: config.type,
            category: config.category,
            difficulty: config.difficulty,
            simulationId,
            durationSeconds
          }),
        }
      );

      if (response.status === 429) {
        toast.error('Muitas requisições. Aguarde alguns segundos.');
        return;
      }
      if (response.status === 402) {
        toast.error('Créditos da IA esgotados. Contate o suporte.');
        return;
      }
      if (!response.ok) {
        throw new Error('Erro ao gerar feedback');
      }

      const data = await response.json();
      setFeedback(data.feedback);
      setStatus('completed');

    } catch (error) {
      console.error('Error ending simulation:', error);
      toast.error('Erro ao finalizar simulação');
    } finally {
      setIsLoading(false);
    }
  }, [simulationId, config, messages]);

  const abandonSimulation = useCallback(async () => {
    if (!simulationId) return;

    try {
      await supabase
        .from('interview_simulations')
        .update({ status: 'abandoned' })
        .eq('id', simulationId);

      setStatus('abandoned');
      setSimulationId(null);
      setMessages([]);
      setConfig(null);
    } catch (error) {
      console.error('Error abandoning simulation:', error);
    }
  }, [simulationId]);

  const resetSimulation = useCallback(() => {
    setSimulationId(null);
    setMessages([]);
    setConfig(null);
    setFeedback(null);
    setStatus(null);
    startTimeRef.current = null;
  }, []);

  return {
    messages,
    isLoading,
    simulationId,
    config,
    feedback,
    status,
    isReady,
    isInitializing,
    isAuthLoading,
    isAuthReady: !!user,
    checkSimulationLimit: () => user ? checkSimulationLimit(user.id) : Promise.resolve({ canSimulate: false, remaining: 0 }),
    startSimulation,
    sendMessage,
    endSimulation,
    abandonSimulation,
    resetSimulation,
  };
}
